// VER-218: user detail + management drawer (Step 11.3 actions; 11.4 adds per-API).
// Every mutation here is security-sensitive, so each runs through withStepUp (the
// re-OTP gate from 11.1). The parent owns the step-up modal + the list refresh.
import { useEffect, useState, useCallback } from "react";
import {
  App,
  Drawer,
  Descriptions,
  Tag,
  Select,
  Segmented,
  Switch,
  Button,
  Space,
  Popconfirm,
  Skeleton,
  Typography,
  Divider,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { usersApi } from "../../console/consoleApi";
import {
  errorMessage,
  roleLabel,
  invitableRolesFor,
  ROLE_LABELS,
  STATUS_COLORS,
} from "../../console/consoleUtils";

const { Text } = Typography;

// withStepUp rejects with this when the admin dismisses the OTP modal — swallow it
// (no error toast) since it's a deliberate cancel, not a failure.
const isCancelled = (err) => err?.message === "STEP_UP_CANCELLED";

export default function UserDetailDrawer({
  userId,
  open,
  onClose,
  onChanged,
  withStepUp,
  actorRole,
}) {
  const { message } = App.useApp();
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null); // pending role selection
  const [savingRole, setSavingRole] = useState(false);
  const [savingPreprod, setSavingPreprod] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Per-API perms editor (11.4): the catalog of collections + the pending selection.
  const [allApis, setAllApis] = useState([]);
  const [perms, setPerms] = useState([]);
  const [apiMode, setApiMode] = useState("all"); // "all" (unrestricted) | "specific"
  const [savingPerms, setSavingPerms] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setUser(null);
    try {
      const res = await usersApi.get(userId);
      const u = res.data.data.user;
      setUser(u);
      setRole(u.role);
      const p = u.apiPerms ?? [];
      setPerms(p);
      setApiMode(p.length ? "specific" : "all"); // empty perms = full access

    } catch (err) {
      message.error(errorMessage(err));
      onClose();
    }
  }, [userId, message, onClose]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Load the grantable API list once when the drawer first opens — bounded by the account's
  // entitlement (VER-222), so an admin can only pick APIs the account actually has.
  useEffect(() => {
    if (!open || allApis.length) return;
    usersApi
      .grantableApis()
      .then((res) => setAllApis(res.data.data.apiCollections))
      .catch(() => {
        /* editor still works; the multi-select just won't suggest options */
      });
  }, [open, allApis.length]);

  // The roles this actor is allowed to assign (mirrors backend invitableRolesFor).
  const assignable = invitableRolesFor(actorRole);
  const roleOptions = assignable.map((r) => ({ value: r, label: ROLE_LABELS[r] || r }));

  const saveRole = async () => {
    if (!role || role === user.role) return;
    setSavingRole(true);
    try {
      const res = await withStepUp((token) => usersApi.changeRole(user.id, role, token));
      setUser(res.data.data.user);
      message.success("Role updated.");
      onChanged?.();
    } catch (err) {
      if (!isCancelled(err)) message.error(errorMessage(err));
      setRole(user.role); // revert the pending selection
    } finally {
      setSavingRole(false);
    }
  };

  const togglePreprod = async (value) => {
    setSavingPreprod(true);
    try {
      const res = await withStepUp((token) => usersApi.setPreprodOnly(user.id, value, token));
      setUser(res.data.data.user);
      message.success(value ? "Restricted to preprod." : "Preprod-only lifted.");
      onChanged?.();
    } catch (err) {
      if (!isCancelled(err)) message.error(errorMessage(err));
    } finally {
      setSavingPreprod(false);
    }
  };

  // "replace" the whole set with the current selection — what's shown is what they
  // get, the least-surprising model for an admin editing a checklist.
  const savePerms = async () => {
    // "All APIs" = replace with an empty list (the backend treats empty as full access).
    const payload = apiMode === "all" ? [] : perms;
    setSavingPerms(true);
    try {
      const res = await withStepUp((token) =>
        usersApi.setApiPerms(user.id, "replace", payload, token)
      );
      const updated = res.data.data.apiPerms;
      setUser((u) => ({ ...u, apiPerms: updated }));
      setPerms(updated);
      setApiMode(updated.length ? "specific" : "all");
      message.success("API access updated.");
      onChanged?.();
    } catch (err) {
      if (!isCancelled(err)) message.error(errorMessage(err));
    } finally {
      setSavingPerms(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await withStepUp((token) => usersApi.remove(user.id, token));
      message.success("User removed.");
      onChanged?.();
      onClose();
    } catch (err) {
      if (!isCancelled(err)) message.error(errorMessage(err));
    } finally {
      setRemoving(false);
    }
  };

  const roleChanged = user && role && role !== user.role;

  // Order-insensitive diff between the pending selection and the saved perms, factoring
  // in the All/Specific mode. "Specific" with nothing chosen can't be saved (it would
  // silently mean "all") — the Save button is disabled in that case.
  const current = user?.apiPerms ?? [];
  const savedMode = current.length ? "specific" : "all";
  const specificChanged =
    perms.length !== current.length || perms.some((p) => !current.includes(p));
  const permsChanged =
    apiMode !== savedMode || (apiMode === "specific" && specificChanged);
  const permsSavable = apiMode === "all" || perms.length > 0;

  return (
    <Drawer
      title={user ? user.email : "User"}
      size={480}
      open={open}
      onClose={onClose}
      destroyOnHidden
    >
      {!user ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : (
        <>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
            <Descriptions.Item label="Name">{user.name || "—"}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={STATUS_COLORS[user.status]}>{user.status}</Tag>
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left" style={{ marginTop: 24 }}>
            Role
          </Divider>
          <Space>
            <Select
              value={role}
              options={roleOptions}
              style={{ width: 200 }}
              onChange={setRole}
              disabled={savingRole}
            />
            <Button type="primary" onClick={saveRole} loading={savingRole} disabled={!roleChanged}>
              Save
            </Button>
          </Space>
          {!assignable.includes(user.role) && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                Current role “{roleLabel(user.role)}” is outside your assignable set.
              </Text>
            </div>
          )}

          <Divider orientation="left" style={{ marginTop: 24 }}>
            Environment access
          </Divider>
          <Space>
            <Switch checked={user.isPreprodOnly} loading={savingPreprod} onChange={togglePreprod} />
            <Text>Restrict to preprod only</Text>
          </Space>
          <div style={{ marginTop: 4 }}>
            <Text type="secondary">
              When on, this user can’t use production API keys — preprod only.
            </Text>
          </div>

          <Divider orientation="left" style={{ marginTop: 24 }}>
            API access
          </Divider>
          <Segmented
            value={apiMode}
            onChange={setApiMode}
            disabled={savingPerms}
            options={[
              { label: "All APIs (full access)", value: "all" },
              { label: "Specific APIs", value: "specific" },
            ]}
          />
          {apiMode === "all" ? (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                This user can call every API, including ones added later.
              </Text>
            </div>
          ) : (
            <Select
              mode="multiple"
              value={perms}
              onChange={setPerms}
              style={{ width: "100%", marginTop: 8 }}
              placeholder="Select APIs"
              options={allApis.map((n) => ({ value: n, label: n }))}
              disabled={savingPerms}
              allowClear
            />
          )}
          <div style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={savePerms}
              loading={savingPerms}
              disabled={!permsChanged || !permsSavable}
            >
              Save API access
            </Button>
            {apiMode === "specific" && perms.length === 0 && (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                Pick at least one API, or choose All APIs.
              </Text>
            )}
          </div>

          <Divider orientation="left" style={{ marginTop: 24 }}>
            Danger zone
          </Divider>
          <Popconfirm
            title="Remove this user?"
            description="They’ll lose portal access and all their sessions end immediately."
            okText="Remove"
            okButtonProps={{ danger: true }}
            onConfirm={remove}
          >
            <Button danger icon={<DeleteOutlined />} loading={removing}>
              Remove user
            </Button>
          </Popconfirm>
        </>
      )}
    </Drawer>
  );
}
